import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
    useGetAccessEventTypesQuery,
    useStoreAccessEventMutation,
} from "../services/cclTokenedAccessEventsApi";
import { useLazyGetPkeyByImKeyQuery } from "../services/cclTokenedEnterpriseCoachingApi";
import {
    AccessEventDetails,
    StoreAccessEventPayload,
} from "../services/types/accessEventTypes";
import { addRecentActivity } from "../app/slices/recentActivitySlice";
import {
    parseEntityFromEventData,
    createRecentActivityItem,
} from "../utilities/recentActivityEntityParser";

export default function useLogAccessEvent() {
    const dispatch = useDispatch();
    const { data: eventTypes, isLoading, isError } = useGetAccessEventTypesQuery();
    const [storeAccessEvent] = useStoreAccessEventMutation();
    const [getPkeyByImKey] = useLazyGetPkeyByImKeyQuery();

    const logEvent = useCallback(
        async (tag: string, data: AccessEventDetails) => {
            if (eventTypes == null || eventTypes.length === 0 || isLoading || isError) return;
            if (tag == null || tag === "") return;

            const type = eventTypes?.find((et) => et.tag === tag);
            if (type == null) return;

            const evtData = data ?? {};
            const evtDataStr = JSON.stringify(evtData);

            const payload: StoreAccessEventPayload = {
                accessEventTypeId: type.id,
                accessEventData: evtDataStr,
            };
            storeAccessEvent(payload);

            // Track for recent activity if this event type is configured for it
            const recentActivityTypeMap: Record<number, string> = {
                1: "session",
                2: "resource",
                3: "user",
                4: "registration",
            };
            const activityType = type.recentActivityType
                ? recentActivityTypeMap[type.recentActivityType]
                : undefined;
            if (activityType) {
                const entityInfo = parseEntityFromEventData(
                    activityType,
                    tag,
                    evtData
                );
                if (entityInfo) {
                    // For resources, we need to fetch the pkey using imKey
                    if (entityInfo.entityType === "resource") {
                        try {
                            const result = await getPkeyByImKey(entityInfo.entityId).unwrap();
                            // Replace entityId (imKey) with pkey
                            const activityItem = createRecentActivityItem(
                                { ...entityInfo, entityId: String(result.pkey) },
                                tag
                            );
                            dispatch(addRecentActivity(activityItem));
                        } catch (error) {
                            // Silently fail - recent activity tracking is non-critical
                            console.warn("Failed to fetch pkey for resource:", error);
                        }
                    } else {
                        // For non-resource entities, proceed as normal
                        const activityItem = createRecentActivityItem(entityInfo, tag);
                        dispatch(addRecentActivity(activityItem));
                    }
                }
            }
        },
        [eventTypes, isError, isLoading, storeAccessEvent, dispatch, getPkeyByImKey],
    );

    return { logEvent };
}
